/**
 * theme/apply.ts —— 偏好 → **呈现层**（浅色／跟随系统；字号 12–17；密度；动效）。
 *
 * 依据：《UI 全局布局壳规范》v1.2 §1ter·三（色板与 token；主题＝浅色／跟随系统，**不提供暗色**）
 *      ＋ §1ter·六（`prefers-reduced-motion` 下全部改直切）。
 * 纪律：只写 **token 变量与 data 属性**；**禁写色值**（色值只在令牌真源 `styles/tokens/`，theme 只读引用）。
 */
import type { Preferences } from "./preferences";
import { clampFontSize, lineHeightFor } from "./tokens";

/** 主题解算结果（记录态；**resolvedScheme 恒为 light** —— 不提供暗色） */
export interface AppliedPresentation {
  theme: Preferences["theme"];
  density: Preferences["density"];
  motion: Preferences["motion"];
  /** 实际呈现的配色：恒浅色（大王 0916 裁决：不提供暗色） */
  resolvedScheme: "light";
  fontSize: number;
  lineHeight: number;
  reducedMotion: boolean;
}

/** 系统是否偏好减弱动效（jsdom 无 matchMedia ⇒ false） */
export function prefersReducedMotion(): boolean {
  try {
    return Boolean(globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  } catch {
    return false;
  }
}

/** 是否跟随系统（口径登记；不引入暗色 token） */
export function followsSystem(theme: Preferences["theme"]): boolean {
  return theme === "system";
}

/** 把偏好落到文档根（幂等：同偏好同结果；不改任何色值） */
export function applyPreferences(
  values: Preferences,
  root?: HTMLElement | null,
): AppliedPresentation {
  const fontSize = clampFontSize(values.font_size);
  const lineHeight = lineHeightFor(fontSize);
  const reducedMotion = prefersReducedMotion();
  const applied: AppliedPresentation = {
    theme: values.theme,
    density: values.density,
    motion: values.motion,
    resolvedScheme: "light",
    fontSize,
    lineHeight,
    reducedMotion,
  };
  if (!root) return applied;
  root.dataset.xgTheme = values.theme;
  root.dataset.xgDensity = values.density;
  root.dataset.xgMotion = values.motion;
  root.dataset.xgScheme = applied.resolvedScheme;
  root.style.setProperty("--xg-font-size-content", fontSize + "px");
  root.style.setProperty("--xg-line-height-content", lineHeight + "px");
  return applied;
}
